import React, { Component } from 'react';
import server from '../../api/server';

// Admin export of a customer's consent record. Lists every versioned snapshot
// (append-only, never deleted) and exports the chosen version to a print-to-PDF
// window embedding the FULL content the customer agreed to: the CBD privacy
// policy text, the data-sharing disclosure, the Equifax credit-check statement +
// the YES/NO answer, and the Mortgage Choice policy link for that version.
class ConsentPDFExport extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loadingVersions: true,
      error: null,
      versions: null,        // array (newest first) or null while loading
      exportingId: null      // version_id currently being exported
    };
  }

  componentDidMount() {
    this.loadVersions();
  }

  loadVersions = async () => {
    const { userId } = this.props;
    if (!userId) {
      this.setState({ loadingVersions: false, versions: [] });
      return;
    }
    try {
      const response = await server.post(`/consent/versions/${userId}`);
      const versions = response.data.versions || [];
      this.setState({ versions, loadingVersions: false });
    } catch (error) {
      console.error('Error loading consent versions:', error);
      this.setState({ loadingVersions: false, versions: [], error: 'Failed to load consent history' });
    }
  };

  exportConsentData = async (versionId) => {
    const { userId } = this.props;
    this.setState({ exportingId: versionId || 'latest', error: null });

    try {
      const response = await server.post(`/consent/export/${userId}`, {
        version_id: versionId || undefined
      });
      this.generatePDF(response.data);
    } catch (error) {
      console.error('Export error:', error);
      this.setState({
        error: error.response?.data?.error || 'Failed to export consent data'
      });
    } finally {
      this.setState({ exportingId: null });
    }
  };

  formatSnapshotDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  safeDateTime = (value) => {
    if (!value) return 'N/A';
    const d = new Date(value);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString('en-AU');
  };

  // Build the document <title>, which becomes the default filename when the
  // admin uses the browser's "Save as PDF". Includes client name, application
  // id and consent date so downloaded files are self-identifying.
  buildFileTitle = (data, version) => {
    const parts = ['Privacy Consent Record'];
    if (data.user?.name) parts.push(data.user.name);
    if (data.user?.application_id) parts.push(data.user.application_id);
    const dateSource = (version && version.submitted_at) || data.exported_at;
    const d = dateSource ? new Date(dateSource) : null;
    if (d && !isNaN(d.getTime())) {
      parts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    // Strip characters browsers reject in download filenames.
    return parts.join(' - ').replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();
  };

  escapeHtml = (str) => String(str == null ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  renderItemHtml = (item) => {
    const isCredit = item.type === 'credit_check';
    const statusHtml = isCredit
      ? `<span class="${item.answer === 'yes' ? 'status' : 'status-bad'}">Credit check: ${item.answer ? item.answer.toUpperCase() : 'N/A'}</span>`
      : `<span class="${item.acknowledged ? 'status' : 'status-bad'}">${item.acknowledged ? 'Acknowledged' : 'Not acknowledged'}</span>`;

    let body = '';
    // item.content is trusted, server-provided registry content (HTML for the
    // CBD policy, plain text for the disclosure / credit statement).
    if (item.content) body += `<div class="policy-content">${item.content}</div>`;
    if (!item.content && !item.url) {
      body += `<p class="meta">No copy of the policy wording was retained for this acknowledgement.</p>`;
    }
    if (item.url) {
      body += `<p><strong>Policy link:</strong> <a href="${this.escapeHtml(item.url)}">${this.escapeHtml(item.url)}</a></p>`;
      body += `<p class="meta">This document is hosted externally; the customer acknowledged the version live at the link above on the consent date.</p>`;
    }

    return `
      <div class="section">
        <h3>${this.escapeHtml(item.title)} <span class="meta">(${item.version ? 'version ' + this.escapeHtml(item.version) : 'version not recorded'})</span></h3>
        <p><strong>Status:</strong> ${statusHtml}</p>
        ${body}
      </div>
    `;
  };

  generatePDF = (data) => {
    const version = data.version;
    const pdfWindow = window.open('', '_blank');
    const fileTitle = this.buildFileTitle(data, version);

    if (!version) {
      pdfWindow.document.write(`
        <html><head><title>${this.escapeHtml(fileTitle)}</title></head>
        <body style="font-family: Arial, sans-serif; margin: 40px;">
          <h1>Privacy Consent Record</h1>
          <p>No consent has been recorded for <strong>${this.escapeHtml(data.user?.name || 'this client')}</strong>.</p>
        </body></html>
      `);
      pdfWindow.document.close();
      return;
    }

    const submittedBy = version.submitted_by || {};
    const itemsHtml = (version.items || []).map(this.renderItemHtml).join('');

    const pdfContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${this.escapeHtml(fileTitle)}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; color: #333; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
            .section { margin-bottom: 25px; padding: 15px; border: 1px solid #ddd; border-radius: 5px; page-break-inside: avoid; }
            .section h3 { margin-top: 0; color: #2c5aa0; border-bottom: 1px solid #eee; padding-bottom: 10px; }
            .policy-content { background:#f8f9fa; padding: 12px 16px; border-radius: 5px; margin: 10px 0; font-size: 13px; }
            .policy-content h4 { margin: 12px 0 4px; color:#1e2129; font-size: 14px; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; font-size: 12px; color: #666; }
            .status { font-weight: bold; color: #28a745; }
            .status-bad { font-weight: bold; color: #dc3545; }
            .meta { color:#888; font-weight: normal; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            @media print { body { margin: 20px; } .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Privacy Consent Record</h1>
            <p>Generated on ${new Date().toLocaleDateString('en-AU')}</p>
          </div>

          ${version.legacy ? `
          <div class="section" style="border-color:#f0ad4e; background:#fffaf0;">
            <h3 style="color:#8a6d3b; border-color:#f5e2bf;">Pre-versioning record</h3>
            <p>This consent was captured before versioned snapshots were introduced.
            The timestamp, IP address and user agent below are the original recorded
            values. The policy wording shown to the customer was <strong>not</strong>
            retained, and no version number was recorded, so this record cannot be
            used to establish which text they agreed to.</p>
          </div>` : ''}

          <div class="section">
            <h3>Client Information</h3>
            <table>
              <tr><th>Name</th><td>${this.escapeHtml(data.user?.name || 'N/A')}</td></tr>
              <tr><th>Email</th><td>${this.escapeHtml(data.user?.email || 'N/A')}</td></tr>
              <tr><th>Application ID</th><td>${this.escapeHtml(data.user?.application_id || 'N/A')}</td></tr>
            </table>
          </div>

          <div class="section">
            <h3>Consent Version</h3>
            <table>
              <tr><th>Version ID</th><td style="word-break: break-all;">${this.escapeHtml(version.version_id)}</td></tr>
              <tr><th>Submitted At</th><td>${this.safeDateTime(version.submitted_at)}</td></tr>
              <tr><th>Expires</th><td>${this.safeDateTime(version.expires_at)}</td></tr>
              <tr><th>Submitted By</th><td>${this.escapeHtml(submittedBy.name || 'N/A')} (${this.escapeHtml(submittedBy.email || 'N/A')})</td></tr>
              <tr><th>IP Address</th><td>${this.escapeHtml(version.ip_address || 'N/A')}</td></tr>
              <tr><th>User Agent</th><td style="word-break: break-all;">${this.escapeHtml(version.user_agent || 'N/A')}</td></tr>
            </table>
          </div>

          ${itemsHtml}

          <div class="section">
            <h3>Export Information</h3>
            <table>
              <tr><th>Exported At</th><td>${this.safeDateTime(data.exported_at)}</td></tr>
              <tr><th>Exported By</th><td>${this.escapeHtml(data.exported_by || 'N/A')}</td></tr>
              <tr><th>Total Versions On Record</th><td>${this.escapeHtml(data.versions_count)}</td></tr>
            </table>
          </div>

          <div class="footer">
            <p>This document contains confidential information and should be handled according to privacy regulations.</p>
            <p>LoanGateway - CBD Mortgage Advisors</p>
          </div>

          <div class="no-print" style="margin-top: 30px; text-align: center;">
            <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer; margin-right: 10px;">Print PDF</button>
            <button onclick="window.close()" style="padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 5px; cursor: pointer;">Close</button>
          </div>
        </body>
      </html>
    `;

    pdfWindow.document.write(pdfContent);
    pdfWindow.document.close();
  };

  render() {
    const { loadingVersions, error, versions, exportingId } = this.state;

    return (
      <div className="consent-pdf-export">
        <div className="table-responsive">
          <table className="table table-head-custom table-head-bg table-borderless table-vertical-center mb-0">
            <thead>
              <tr className="text-left text-uppercase">
                <th>Snapshot</th>
                <th>Credit Check</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingVersions ? (
                <tr>
                  <td colSpan={3} className="text-muted">Loading versions...</td>
                </tr>
              ) : null}

              {!loadingVersions && (!versions || versions.length === 0) ? (
                <tr>
                  <td colSpan={3} className="text-muted">No consent records.</td>
                </tr>
              ) : null}

              {!loadingVersions && (versions || []).map((v, i) => (
                <tr key={v.version_id}>
                  <td>
                    <span className="text-dark-75 font-weight-bolder d-block font-size-lg">
                      {this.formatSnapshotDate(v.submitted_at)}
                    </span>
                    <span className="text-muted font-weight-bold font-size-sm">
                      Expires {this.formatSnapshotDate(v.expires_at)}
                    </span>
                    {i === 0 && !v.legacy ? (
                      <span className="ml-2 label label-light-primary label-inline">Latest</span>
                    ) : null}
                    {v.legacy ? (
                      <span className="ml-2 label label-light-warning label-inline">Pre-versioning</span>
                    ) : null}
                    {v.expired ? (
                      <span className="ml-2 label label-light-danger label-inline">Expired</span>
                    ) : null}
                  </td>
                  <td>
                    <span className="text-muted">
                      {v.credit_check ? v.credit_check.toUpperCase() : 'N/A'}
                    </span>
                  </td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="btn btn-icon btn-light btn-hover-primary btn-sm"
                      title="Export / save as PDF"
                      disabled={exportingId === v.version_id}
                      onClick={() => this.exportConsentData(v.version_id)}
                    >
                      {exportingId === v.version_id ? (
                        <span className="spinner-border spinner-border-sm text-primary" role="status" />
                      ) : (
                        <i className="fas fa-print"></i>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {error && (
          <div className="mt-2 text-danger font-size-sm">
            {error}
          </div>
        )}
      </div>
    );
  }
}

export default ConsentPDFExport;
